import React from 'react';
import { useApp } from '../context/AppContext';
import {
  LayoutDashboard,
  BookOpen,
  BarChart3,
  UserCheck,
  Shield,
  HelpCircle,
  Layers,
  FileSpreadsheet,
} from 'lucide-react';

export const MobileNav: React.FC = () => {
  const { currentUser, currentView, setCurrentView, setAuthModalOpen, attempts } = useApp();

  const isStudent = !currentUser || currentUser.role === 'student';

  return (
    <nav
      id="mobile-bottom-nav"
      className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-md border-t border-[#E5E9E9] px-2 py-1.5 shadow-[0_-4px_16px_rgba(17,17,17,0.03)]"
      style={{ paddingBottom: 'max(0.375rem, env(safe-area-inset-bottom))' }}
    >
      <div className="grid grid-cols-4 items-center justify-around text-center">
        {isStudent ? (
          <>
            <button
              id="mobile-nav-home"
              onClick={() => setCurrentView('student-dashboard')}
              className={`flex flex-col items-center justify-center py-1 px-1 rounded-xl transition-all cursor-pointer ${
                currentView === 'student-dashboard'
                  ? 'text-[#111111] font-bold'
                  : 'text-[#5F6368] hover:text-[#111111]'
              }`}
            >
              <div
                className={`w-9 h-7 flex items-center justify-center rounded-lg transition-colors ${
                  currentView === 'student-dashboard' ? 'bg-[#E8FAF9] text-[#1fb4ac]' : ''
                }`}
              >
                <LayoutDashboard className="w-5 h-5" />
              </div>
              <span className="text-[10px] tracking-tight leading-none mt-1">Home</span>
            </button>

            <button
              id="mobile-nav-sets"
              onClick={() => setCurrentView('student-dashboard')}
              className={`flex flex-col items-center justify-center py-1 px-1 rounded-xl transition-all cursor-pointer ${
                currentView === 'quiz-revision' || currentView === 'quiz-test'
                  ? 'text-[#111111] font-bold'
                  : 'text-[#5F6368] hover:text-[#111111]'
              }`}
            >
              <div
                className={`w-9 h-7 flex items-center justify-center rounded-lg transition-colors ${
                  currentView === 'quiz-revision' || currentView === 'quiz-test' ? 'bg-[#E8FAF9] text-[#1fb4ac]' : ''
                }`}
              >
                <BookOpen className="w-5 h-5" />
              </div>
              <span className="text-[10px] tracking-tight leading-none mt-1">Practice</span>
            </button>

            <button
              id="mobile-nav-analytics"
              onClick={() => setCurrentView('student-analytics')}
              className={`flex flex-col items-center justify-center py-1 px-1 rounded-xl relative transition-all cursor-pointer ${
                currentView === 'student-analytics'
                  ? 'text-[#111111] font-bold'
                  : 'text-[#5F6368] hover:text-[#111111]'
              }`}
            >
              <div
                className={`w-9 h-7 flex items-center justify-center rounded-lg transition-colors relative ${
                  currentView === 'student-analytics' ? 'bg-[#E8FAF9] text-[#1fb4ac]' : ''
                }`}
              >
                <BarChart3 className="w-5 h-5" />
                {attempts.length > 0 && (
                  <span className="absolute -top-0.5 -right-1 w-2 h-2 bg-[#56E8E1] rounded-full" />
                )}
              </div>
              <span className="text-[10px] tracking-tight leading-none mt-1">Progress</span>
            </button>

            <button
              id="mobile-nav-account"
              onClick={() => setAuthModalOpen(true)}
              className="flex flex-col items-center justify-center py-1 px-1 rounded-xl text-[#5F6368] hover:text-[#111111] transition-all cursor-pointer"
            >
              <div className="w-9 h-7 flex items-center justify-center rounded-lg">
                <div className="w-5 h-5 rounded-full bg-[#E8FAF9] text-[#1b8e88] font-bold text-[10px] flex items-center justify-center border border-[#56E8E1]/40">
                  {currentUser?.name ? currentUser.name.charAt(0) : '?'}
                </div>
              </div>
              <span className="text-[10px] tracking-tight leading-none mt-1">Account</span>
            </button>
          </>
        ) : (
          <>
            <button
              id="mobile-nav-admin-dashboard"
              onClick={() => setCurrentView('admin-dashboard')}
              className={`flex flex-col items-center justify-center py-1 px-1 rounded-xl transition-all cursor-pointer ${
                currentView === 'admin-dashboard'
                  ? 'text-[#111111] font-bold'
                  : 'text-[#5F6368] hover:text-[#111111]'
              }`}
            >
              <div
                className={`w-9 h-7 flex items-center justify-center rounded-lg ${
                  currentView === 'admin-dashboard' ? 'bg-[#E8FAF9] text-[#1fb4ac]' : ''
                }`}
              >
                <LayoutDashboard className="w-5 h-5" />
              </div>
              <span className="text-[10px] tracking-tight leading-none mt-1">Overview</span>
            </button>

            <button
              id="mobile-nav-admin-users"
              onClick={() => setCurrentView('admin-users')}
              className={`flex flex-col items-center justify-center py-1 px-1 rounded-xl transition-all cursor-pointer ${
                currentView === 'admin-users'
                  ? 'text-[#111111] font-bold'
                  : 'text-[#5F6368] hover:text-[#111111]'
              }`}
            >
              <div
                className={`w-9 h-7 flex items-center justify-center rounded-lg ${
                  currentView === 'admin-users' ? 'bg-[#E8FAF9] text-[#1fb4ac]' : ''
                }`}
              >
                <UserCheck className="w-5 h-5" />
              </div>
              <span className="text-[10px] tracking-tight leading-none mt-1">Users</span>
            </button>

            <button
              id="mobile-nav-admin-questions"
              onClick={() => setCurrentView('admin-questions')}
              className={`flex flex-col items-center justify-center py-1 px-1 rounded-xl transition-all cursor-pointer ${
                currentView === 'admin-questions'
                  ? 'text-[#111111] font-bold'
                  : 'text-[#5F6368] hover:text-[#111111]'
              }`}
            >
              <div
                className={`w-9 h-7 flex items-center justify-center rounded-lg ${
                  currentView === 'admin-questions' ? 'bg-[#E8FAF9] text-[#1fb4ac]' : ''
                }`}
              >
                <HelpCircle className="w-5 h-5" />
              </div>
              <span className="text-[10px] tracking-tight leading-none mt-1">Questions</span>
            </button>

            <button
              id="mobile-nav-admin-sets"
              onClick={() => setCurrentView('admin-sets')}
              className={`flex flex-col items-center justify-center py-1 px-1 rounded-xl transition-all cursor-pointer ${
                currentView === 'admin-sets'
                  ? 'text-[#111111] font-bold'
                  : 'text-[#5F6368] hover:text-[#111111]'
              }`}
            >
              <div
                className={`w-9 h-7 flex items-center justify-center rounded-lg ${
                  currentView === 'admin-sets' ? 'bg-[#E8FAF9] text-[#1fb4ac]' : ''
                }`}
              >
                <Layers className="w-5 h-5" />
              </div>
              <span className="text-[10px] tracking-tight leading-none mt-1">Sets</span>
            </button>

            <button
              id="mobile-nav-admin-attempts"
              onClick={() => setCurrentView('admin-attempts')}
              className={`flex flex-col items-center justify-center py-1 px-1 rounded-xl transition-all cursor-pointer ${
                currentView === 'admin-attempts'
                  ? 'text-[#111111] font-bold'
                  : 'text-[#5F6368] hover:text-[#111111]'
              }`}
            >
              <div
                className={`w-9 h-7 flex items-center justify-center rounded-lg ${
                  currentView === 'admin-attempts' ? 'bg-[#E8FAF9] text-[#1fb4ac]' : ''
                }`}
              >
                <FileSpreadsheet className="w-5 h-5" />
              </div>
              <span className="text-[10px] tracking-tight leading-none mt-1">Results</span>
            </button>
          </>
        )}
      </div>
    </nav>
  );
};
